import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Tag, Alert } from 'antd';
import { PlusOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { avaliacoesAdminService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import NovaAvaliacaoModal from './NovaAvaliacaoModal';

// Contrato real: GET /api/avaliacoes/periodos (PeriodoAvaliacaoAdminResponse).
// Criação (ajuste pre-producao) usa o endpoint orquestrador
// POST /api/avaliacoes/criar-com-turmas -- já entrega o período ABERTO,
// com snapshot de critérios e turmas selecionadas materializadas (ver
// NovaAvaliacaoModal.js). O endpoint antigo de criar período "vazio" em
// RASCUNHO (POST /api/avaliacoes/periodos) continua existindo no backend
// (compatibilidade), mas não faz mais parte da experiência principal --
// edição de um período RASCUNHO isolado continua acessível pela aba
// Resumo do detalhe, para quem chegou lá por outro caminho.
export const STATUS_PERIODO_INFO = {
  RASCUNHO: { label: 'Rascunho', color: 'default' },
  ABERTO: { label: 'Aberto', color: 'green' },
  ENCERRADO: { label: 'Encerrado', color: 'default' },
};

const formatarData = (valor) => (valor ? dayjs(valor).format('DD/MM/YYYY') : '—');

const PeriodosAdminPainel = () => {
  const navigate = useNavigate();
  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await avaliacoesAdminService.listarPeriodos();
      setPeriodos(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Não foi possível carregar os períodos.');
      setErro(mensagem);
      setPeriodos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleAvaliacaoCriada = (periodoId) => {
    carregar();
    navigate(`/avaliacoes/periodos/${periodoId}`);
  };

  const columns = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    { title: 'Data de referência', dataIndex: 'data_referencia', key: 'data_referencia', render: formatarData },
    { title: 'Abertura', dataIndex: 'data_abertura', key: 'data_abertura', render: formatarData },
    { title: 'Encerramento', dataIndex: 'data_encerramento', key: 'data_encerramento', render: formatarData },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (valor) => {
        const info = STATUS_PERIODO_INFO[valor] || { label: valor, color: 'default' };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 140,
      render: (_, registro) => (
        <Button
          size="small"
          type="primary"
          ghost
          icon={<RightOutlined />}
          iconPosition="end"
          onClick={() => navigate(`/avaliacoes/periodos/${registro.id}`)}
        >
          Ver detalhes
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalAberto(true)}>
          Nova avaliação
        </Button>
      </div>

      {erro && (
        <Alert
          type="error"
          showIcon
          message="Não foi possível carregar"
          description={erro}
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          }
        />
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={erro ? [] : periodos}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{ emptyText: erro ? ' ' : 'Nenhuma avaliação cadastrada.' }}
        scroll={{ x: 900 }}
      />

      <NovaAvaliacaoModal
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        onCriada={handleAvaliacaoCriada}
      />
    </div>
  );
};

export default PeriodosAdminPainel;
